// Based on https://github.com/GrooveHQ/internal-design-system/blob/master/src/pages/components/DeleteRow/DeleteRow.jsx
import React, { useCallback, useState } from 'react'
import { bool, func, oneOfType, string, node } from 'prop-types'
import { AdminAccessDrawer } from 'subapps/settings/components/drawers/NoAccess'
import Field from '@groovehq/internal-design-system/lib/components/Field/Field'
import ModalBtns from '@groovehq/internal-design-system/lib/components/ModalBtns/ModalBtns'
import AnimatedEllipsis from '@groovehq/internal-design-system/lib/components/AnimatedEllipsis/AnimatedEllipsis'

import {
  text,
  paragraph,
} from '@groovehq/internal-design-system/lib/styles/elements'
import { delay, isPromise } from 'util/functions'

const EntityDelete = ({
  title,
  deleteBtnText,
  deleteBtnDisabled,
  hardDelete,
  hardDeleteWord = 'DELETE',
  open,
  onClose,
  onCancel,
  onRemove,
  tertiaryBtnText,
  children,
  isNoResultFound,
  isLoading,
}) => {
  const [typed, setTyped] = useState(null)
  const [isRemoving, setIsRemoving] = useState(false)
  const [focused, setFocused] = useState(false)
  const hasValue = typed && typed !== ''

  let isValid = false
  if (typed === hardDeleteWord) isValid = true
  const isDeleteButtonDisabled =
    deleteBtnDisabled || (hardDelete && !isValid) || isRemoving

  const handleHardDeleteFieldChange = useCallback(
    e => {
      setTyped(e.target.value)
    },
    [setTyped]
  )

  const handleHardDeleteFieldFocus = useCallback(() => setFocused(true), [
    setFocused,
  ])
  const handleHardDeleteFieldBlur = useCallback(() => setFocused(false), [
    setFocused,
  ])

  const handleDelete = useCallback(
    async () => {
      if (isRemoving) return false
      setIsRemoving(true)
      const response = onRemove()
      if (isPromise(response)) {
        await response
        setIsRemoving(false)
      } else {
        delay(() => setIsRemoving(false), 200)
      }
      onClose({ isAfterDelete: true })
      return true
    },
    [onRemove, onClose, setIsRemoving, isRemoving]
  )

  const footer = (
    <ModalBtns
      warningBtnText={
        <>
          {!isRemoving && <span>{deleteBtnText}</span>}
          {isRemoving && (
            <span>
              Deleting<AnimatedEllipsis />
            </span>
          )}
        </>
      }
      warningBtnDisabled={isDeleteButtonDisabled}
      onClickWarningBtn={handleDelete}
      onClickTertiaryBtn={onCancel}
      tertiaryBtnText={tertiaryBtnText}
    />
  )

  return (
    <AdminAccessDrawer
      title={title}
      open={open}
      onClose={onClose}
      footer={footer}
      isNoResultFound={isNoResultFound}
      isLoading={isLoading}
    >
      <div css={text.styles.textNormal} className="grui pt-10">
        {children}
        {hardDelete !== true && (
          <>
            <br />
            <p css={paragraph.styles.preflight}>
              Are you sure you want to proceed with this action?
            </p>
          </>
        )}
        {hardDelete === true && (
          <Field
            required
            equals={hardDeleteWord}
            className="grui mt-14"
            label={`Type ${hardDeleteWord} to continue`}
            onChange={handleHardDeleteFieldChange}
            onFocus={handleHardDeleteFieldFocus}
            onBlur={handleHardDeleteFieldBlur}
            validateStatus={hasValue && !isValid && 'error'}
            help={
              hasValue &&
              !isValid &&
              !focused &&
              `The value must equal ${hardDeleteWord}`
            }
          />
        )}
      </div>
    </AdminAccessDrawer>
  )
}

EntityDelete.propTypes = {
  title: string,
  deleteBtnText: string,
  open: bool.isRequired,
  onClose: func.isRequired,
  onRemove: func,
  children: oneOfType([string, node]),
}

EntityDelete.defaultProps = {
  title: 'Delete',
  onRemove: () => {},
  children: undefined,
  deleteBtnText: 'Delete',
}

export default EntityDelete
